<template>
  <div class="mp" data-test="pfe-preview-body">
    <!-- TEXT: a phone with the bubble. Links are underlined, exactly the URL the patient would get. -->
    <div v-if="channel === 'text'" class="mp-stage">
      <div class="mp-phone" data-test="pfe-preview-sms">
        <div class="mp-screen">
          <div class="mp-status"><span>3:12</span><span class="mp-notch"></span><span class="mp-status-gap"></span></div>
          <div class="mp-contact">
            <div class="mp-avatar">{{ initials }}</div>
            <div class="mp-contact-name">{{ siteName }}</div>
          </div>
          <div class="mp-thread">
            <div class="mp-time">Today 3:12 PM</div>
            <div class="mp-bubble" data-test="pfe-preview-bubble"><template v-for="(p, i) in smsParts"><span :key="i" :class="{ 'mp-link': p.link }">{{ p.text }}</span></template></div>
          </div>
        </div>
      </div>
    </div>

    <!-- EMAIL: from the practice at its REAL verified sender, with the practice's REAL signature (or the dashed note when it is off). -->
    <div v-else class="mp-email" data-test="pfe-preview-email">
      <div class="mp-meta">
        <span class="mp-meta-k">From</span>
        <span><strong>{{ siteName }}</strong> <span class="mp-muted" data-test="pfe-preview-from">&lt;{{ site.senderEmail || 'no verified sender yet' }}&gt;</span></span>
        <span class="mp-meta-k">To</span><span>Maria Lopez</span>
        <span class="mp-meta-k">Subject</span><strong data-test="pfe-preview-subject">{{ emailView.subject }}</strong>
      </div>
      <div class="mp-mail-body">
        <template v-for="(b, i) in emailView.blocks">
          <p v-if="b.type === 'p'" :key="i" class="mp-p">{{ b.text }}</p>
          <div v-else-if="b.type === 'links'" :key="i" class="mp-cards" data-test="pfe-preview-links">
            <div v-for="f in b.forms" :key="f.url" class="mp-card">
              <i class="fa fa-file-text-o" aria-hidden="true"></i>
              <div class="mp-card-text">
                <div class="mp-card-label">{{ f.label }}</div>
                <div class="mp-card-title">{{ f.title }}</div>
              </div>
            </div>
          </div>
          <!-- Forms Settings: the message's ONE form link as a card (the picked form / packet's name; the link itself is minted at send time) -->
          <div v-else-if="b.type === 'formLink'" :key="i" class="mp-cards" data-test="pfe-preview-form-link">
            <div class="mp-card">
              <i class="fa fa-files-o" aria-hidden="true"></i>
              <div class="mp-card-text">
                <div class="mp-card-label" data-test="pfe-preview-form-link-url">{{ formLinkUrl }}</div>
                <div class="mp-card-title" data-test="pfe-preview-form-link-title">{{ b.title }}</div>
              </div>
            </div>
          </div>
          <!-- the office's own stored signature HTML, verbatim (the same markup Settings → Email previews) -->
          <div v-else-if="b.type === 'signature'" :key="i" class="mp-sig" data-test="pfe-preview-signature" v-html="site.signatureHtml"></div>
          <div v-else :key="i" class="mp-sig-off" data-test="pfe-preview-signature-off">
            <i class="fa fa-info-circle" aria-hidden="true"></i>Your email signature is off &mdash; turn it on in Settings &rarr; Email.
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import { previewEmail, previewSmsParts } from '@/utils/postOp/message';

/**
 * Live preview of the shared message — a phone bubble for the text, an email card for the email (design: "Preview").
 * The patient is the fictional "Maria Lopez" (no PHI in a settings screen); the PRACTICE is the site's real name / phone /
 * verified sender / signature. All rendering rules live in utils/postOp/message.js and mirror the sender's render.js.
 */
export default {
  name: 'PfeMessagePreview',
  props: {
    channel: { type: String, default: 'text' },
    /** 1 or 2 sample procedures → one link or two. */
    count: { type: Number, default: 1 },
    smsBody: { type: String, default: '' },
    email: { type: Object, default: () => ({ subject: '', body: '' }) },
    /** { name, phone, senderEmail, signatureEnabled, signatureHtml } */
    site: { type: Object, default: () => ({}) },
    /** Forms Settings only: extra sample values ({{apt_date}}, {{form_link}}) merged over the post-op ones. null ⇒ the post-op preview, unchanged. */
    vars: { type: Object, default: null },
    /** Forms Settings only: a string makes an email paragraph that is ONLY {{form_link}} a link card with this title. null ⇒ unchanged. */
    formLinkTitle: { type: String, default: null },
  },
  computed: {
    extra() {
      if (!this.vars && this.formLinkTitle === null) return undefined; // the post-op preview, byte for byte
      return { vars: this.vars || undefined, formLinkTitle: this.formLinkTitle === null ? undefined : this.formLinkTitle };
    },
    /** The masked link under the card's title (a bare host + path, as the board draws it). */
    formLinkUrl() { return String((this.vars && this.vars.form_link) || '').replace(/^https?:\/\//, ''); },
    siteName() { return this.site.name || 'Your practice'; },
    initials() {
      const words = this.siteName.split(/\s+/).filter(Boolean);
      return (words.length > 1 ? words[0][0] + words[1][0] : (words[0] || '?').slice(0, 2)).toUpperCase();
    },
    smsParts() { return previewSmsParts(this.smsBody, this.site, this.count, this.extra); },
    emailView() { return previewEmail(this.email, this.site, this.count, this.extra); },
  },
};
</script>

<style lang="scss" scoped>
@import './editor';

.mp-stage {
  display: flex;
  justify-content: center;
  padding: 24px 12px;
  background: $pfe-neutral-50;
  border-radius: 12px;
}
.mp-phone {
  width: 290px;
  height: 540px;
  border-radius: 44px;
  background: #0c0f0e;
  padding: 9px;
  box-shadow: 0 4px 14px rgba(12, 15, 14, 0.2);
}
.mp-screen {
  height: 100%;
  border-radius: 36px;
  background: $pp-surface;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.mp-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 26px 0;
  font-size: 13px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.mp-notch { width: 76px; height: 22px; border-radius: 12px; background: #0c0f0e; }
.mp-status-gap { width: 24px; }
.mp-contact {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px 8px 10px;
  border-bottom: 1px solid $pp-border;
}
.mp-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #dfe4e3;
  color: $pfe-neutral-600;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 800;
}
.mp-contact-name { max-width: 100%; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 12px; font-weight: 600; }
.mp-thread { flex: 1; padding: 14px 12px; display: flex; flex-direction: column; gap: 8px; overflow: hidden; }
.mp-time { text-align: center; font-size: 11px; color: $pfe-neutral-500; }
.mp-bubble {
  align-self: flex-start;
  max-width: 86%;
  padding: 10px 13px;
  border-radius: 18px 18px 18px 6px;
  background: $pfe-neutral-100;
  font-size: 14px;
  line-height: 1.42;
  white-space: pre-wrap;
  word-break: break-word;
}
.mp-link { color: $pfe-link-blue; text-decoration: underline; }

.mp-email { border: 1px solid $pp-border; border-radius: 12px; overflow: hidden; }
.mp-meta {
  display: grid;
  grid-template-columns: 64px minmax(0, 1fr);
  gap: 4px 10px;
  padding: 14px 18px;
  border-bottom: 1px solid $pp-border;
  background: $pfe-neutral-25;
  font-size: 13px;
}
.mp-meta-k { color: $pfe-neutral-500; }
.mp-muted { color: $pfe-neutral-500; }
.mp-mail-body { padding: 22px 24px 24px; font-size: 14.5px; line-height: 1.6; color: $pfe-neutral-800; }
.mp-p { margin: 0 0 14px; white-space: pre-wrap; }
.mp-cards { margin: 0 0 16px; display: flex; flex-direction: column; gap: 8px; }
.mp-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border: 1px solid $pp-border;
  border-radius: 10px;

  > i { color: $pfe-neutral-500; font-size: 17px; }
}
.mp-card-text { flex: 1; min-width: 0; }
.mp-card-label { font-size: 12px; color: $pfe-neutral-500; }
.mp-card-title { font-weight: 700; color: $pfe-link-blue; text-decoration: underline; }
.mp-sig { margin-top: 6px; padding-top: 16px; border-top: 1px solid $pp-border; overflow-x: auto; }
.mp-sig-off {
  margin-top: 6px;
  padding: 12px 14px;
  border: 1px dashed $pp-border-strong;
  border-radius: 10px;
  background: $pfe-neutral-25;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: $pfe-neutral-600;

  i { color: $pfe-neutral-400; }
}
</style>
